import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "sonner";
import { endpoints } from "../../../hooks/endpoints";
import { getToken, isEmailValid, isPhoneValid } from "../../../hooks/utils";
import { useAppDispatch } from "../../../store/hooks";
import {
  AssistantPermissions,
  invokeUserRefetch,
} from "../../../store/slices/storeSlice";
import Separator from "../../Separator";
import CategorySelector from "./CategorySelector";

const CreateAssistant = () => {
  const dispatch = useAppDispatch();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  // Permissions
  const [permissions, setPermissions] = useState<AssistantPermissions>({
    can_create_quizzes: false,
    can_create_homework: false,
    can_view_quiz_grades: false,
    can_view_homework_grades: false,
    can_renew_subscription: false,
    can_publish_lessons: [],
    can_edit_lessons: [],
    can_view_sales: [],
    can_create_codes: [],
    can_manage_students: [],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!name || name.length < 3) {
      setError("يجب إدخال اسم صحيح (على الأقل 3 أحرف)");
      return;
    }

    const emailValidation = isEmailValid(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message);
      return;
    }

    const phoneValidation = isPhoneValid(phone);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.message);
      return;
    }

    if (!password || password.length < 6) {
      setError("يجب أن تكون كلمة المرور على الأقل 6 أحرف");
      return;
    }

    setIsPending(true);

    try {
      const res = await fetch(endpoints.createAssistant, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        method: "POST",
        body: JSON.stringify({
          name,
          email,
          phone,
          password,
          permissions,
        }),
      });

      const response = await res.json();

      if (!res.ok) {
        throw new Error(response.message || response.data || "حدث خطأ");
      }

      toast.success("تم إنشاء المدرس المساعد بنجاح");

      // Reset form
      setName("");
      setEmail("");
      setPhone("");
      setPassword("");
      setPermissions({
        can_create_quizzes: false,
        can_create_homework: false,
        can_view_quiz_grades: false,
        can_view_homework_grades: false,
        can_renew_subscription: false,
        can_publish_lessons: [],
        can_edit_lessons: [],
        can_view_sales: [],
        can_create_codes: [],
        can_manage_students: [],
      });

      dispatch(invokeUserRefetch());
    } catch (err: any) {
      setError(err.message || "حدث خطأ أثناء إنشاء المدرس المساعد");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <form
      className="p-3 rounded-lg border shadow-sm bg-white"
      onSubmit={handleSubmit}
    >
      <h4 className="p-2 mb-4 border-b-2 border-primary w-fit font-bold">
        إنشاء مساعد جديد
      </h4>

      {/* Basic Information */}
      <div className="mb-4">
        <label htmlFor="assistant-name" className="block font-bold mb-2">
          الاسم <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="assistant-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="أدخل اسم المدرس المساعد"
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="assistant-email" className="block font-bold mb-2">
          البريد الإلكتروني <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="assistant-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="example@domain.com"
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="assistant-phone" className="block font-bold mb-2">
          رقم الهاتف <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          id="assistant-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="01xxxxxxxxx"
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary text-end"
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="assistant-password" className="block font-bold mb-2">
          كلمة المرور <span className="text-red-500">*</span>
        </label>
        <input
          type="password"
          id="assistant-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="أدخل كلمة المرور (على الأقل 6 أحرف)"
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
          required
        />
      </div>

      <Separator />

      {/* Permissions */}
      <h5 className="font-bold mb-3 text-lg">الصلاحيات</h5>

      <div className="mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={permissions.can_create_quizzes}
            onChange={(e) =>
              setPermissions({
                ...permissions,
                can_create_quizzes: e.target.checked,
              })
            }
            className="w-4 h-4"
          />
          <span className="font-medium">يمكنه إنشاء امتحانات</span>
        </label>
      </div>

      <div className="mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={permissions.can_create_homework}
            onChange={(e) =>
              setPermissions({
                ...permissions,
                can_create_homework: e.target.checked,
              })
            }
            className="w-4 h-4"
          />
          <span className="font-medium">يمكنه إنشاء واجبات</span>
        </label>
      </div>

      <div className="mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={permissions.can_view_quiz_grades}
            onChange={(e) =>
              setPermissions({
                ...permissions,
                can_view_quiz_grades: e.target.checked,
              })
            }
            className="w-4 h-4"
          />
          <span className="font-medium">يمكنه عرض درجات الامتحانات</span>
        </label>
      </div>

      <div className="mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={permissions.can_view_homework_grades}
            onChange={(e) =>
              setPermissions({
                ...permissions,
                can_view_homework_grades: e.target.checked,
              })
            }
            className="w-4 h-4"
          />
          <span className="font-medium">يمكنه عرض درجات الواجبات</span>
        </label>
      </div>

      <div className="mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={permissions.can_renew_subscription}
            onChange={(e) =>
              setPermissions({
                ...permissions,
                can_renew_subscription: e.target.checked,
              })
            }
            className="w-4 h-4"
          />
          <span className="font-medium">يمكنه تجديد إشتراك المنصة</span>
        </label>
      </div>

      <Separator />

      <CategorySelector
        title="يمكنه نشر حصص في الأقسام التالية"
        selectedCategories={permissions.can_publish_lessons}
        onChange={(cats) =>
          setPermissions({ ...permissions, can_publish_lessons: cats })
        }
      />

      <CategorySelector
        title="يمكنه تعديل حصص في الأقسام التالية"
        selectedCategories={permissions.can_edit_lessons}
        onChange={(cats) =>
          setPermissions({ ...permissions, can_edit_lessons: cats })
        }
      />

      <CategorySelector
        title="يمكنه عرض مبيعات الأقسام التالية"
        selectedCategories={permissions.can_view_sales}
        onChange={(cats) =>
          setPermissions({ ...permissions, can_view_sales: cats })
        }
      />

      <CategorySelector
        title="يمكنه إنشاء أكواد للأقسام التالية"
        selectedCategories={permissions.can_create_codes}
        onChange={(cats) =>
          setPermissions({ ...permissions, can_create_codes: cats })
        }
      />

      <CategorySelector
        title="يمكنه إدارة الطلاب الذين اشتروا من الأقسام التالية"
        selectedCategories={permissions.can_manage_students}
        onChange={(cats) =>
          setPermissions({ ...permissions, can_manage_students: cats })
        }
      />

      <Separator />

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={isPending}>
          {isPending ? <Spinner variant="button" /> : "إنشاء المدرس المساعد"}
        </Button>
      </div>

      {!!error && (
        <div
          className="bg-red-100 text-red-700 rounded-lg px-4 py-2 mt-3 w-fit"
          role="alert"
        >
          {error}
        </div>
      )}
    </form>
  );
};

export default CreateAssistant;
